import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { canAccess, Role, SidebarFeature } from '../config/roleAccess';
import LoadingSpinner from './ui/LoadingSpinner';

/**
 * RouteGuard — soft authorization wrapper for protected routes.
 *
 * Reads the current user's `role` (from AuthContext / `/auth/me`) and
 * checks it against the ADR-0004 decision matrix via `canAccess()`.
 * If the user is not authorised for the given feature, redirects to
 * `/app/forbidden` with state describing the attempted destination.
 *
 * This complements the sidebar visibility gating: the sidebar hides
 * links the user can't use, but a deep link (typed URL, bookmark,
 * shared link, browser history) can still land them on a page they
 * shouldn't see. RouteGuard catches that case.
 *
 * Backend enforcement remains the source of truth for data access —
 * the scoping helper (`apply_scope`) returns 403/empty results
 * regardless of what the frontend renders. RouteGuard is purely UX.
 */
interface RouteGuardProps {
  feature: SidebarFeature;
  children: React.ReactNode;
}

export function RouteGuard({ feature, children }: RouteGuardProps) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const role = (user?.role ?? null) as Role | null;
  if (!canAccess(role, feature)) {
    return (
      <Navigate
        to="/app/forbidden"
        replace
        state={{ from: location.pathname, feature, role }}
      />
    );
  }

  return <>{children}</>;
}

export default RouteGuard;
